import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { User, Mail, Shield, Eye, EyeOff, Save, Building2, Phone, FileText, MapPin } from 'lucide-react';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    owner_name: '',
    phone: '',
    email: '',
    tax_office: '',
    tax_number: '',
    address: '',
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      const response = await authAPI.getCompanyProfile();
      setCompanyForm({
        name: response.data.name || '',
        owner_name: response.data.owner_name || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        tax_office: response.data.tax_office || '',
        tax_number: response.data.tax_number || '',
        address: response.data.address || '',
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Firma bilgileri alinamadi');
    }
  };

  const onSubmitProfile = async (data) => {
    setIsLoading(true);
    try {
      await updateProfile({
        email: data.email,
      });
      toast.success('Profil basariyla guncellendi!');
    } catch (error) {
      toast.error('Profil guncellenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitPassword = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Sifreler eslesmiyor');
      return;
    }

    setIsLoading(true);
    try {
      await updateProfile({
        password: data.newPassword,
      });
      toast.success('Sifre basariyla guncellendi!');
      // Reset password fields
      document.getElementById('currentPassword').value = '';
      document.getElementById('newPassword').value = '';
      document.getElementById('confirmPassword').value = '';
    } catch (error) {
      toast.error('Sifre guncellenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitCompany = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    try {
      await authAPI.updateCompanyProfile(companyForm);
      toast.success('Firma bilgileri basariyla guncellendi!');
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      toast.error(validationMessage || error.response?.data?.error || 'Firma bilgileri guncellenemedi');
    } finally {
      setCompanyLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Profil Ayarlari</h1>
        <p className="text-gray-600 mt-2">Hesap ayarlarinizi ve tercihlerinizi yonetin</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{user?.username}</h2>
              <p className="text-gray-500 capitalize">{user?.role}</p>
              <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
                <Shield className="w-4 h-4 mr-1" />
                <span>Kayit tarihi {new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profil Bilgileri */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Profil Bilgileri</h3>
            </div>
            <form onSubmit={handleSubmit(onSubmitProfile)} className="p-6 space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Kullanici Adi
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    {...register('username')}
                    type="text"
                    id="username"
                    disabled
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Kullanici adi degistirilemez</p>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    {...register('email', {
                      required: 'E-posta zorunludur',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Gecersiz e-posta adresi'
                      }
                    })}
                    type="email"
                    id="email"
                    className={`pl-10 w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Degisiklikleri Kaydet
                </button>
              </div>
            </form>
          </div>

          {/* Sifre Degistir */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Sifre Degistir</h3>
            </div>
            <form onSubmit={handleSubmit(onSubmitPassword)} className="p-6 space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Yeni Sifre
                </label>
                <div className="relative">
                  <input
                    {...register('newPassword', {
                      minLength: {
                        value: 6,
                        message: 'Sifre en az 6 karakter olmali'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-sm text-red-600 mt-1">{errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Yeni Sifre
                </label>
                <input
                  {...register('confirmPassword', {
                    validate: value => value === newPassword || 'Sifreler eslesmiyor'
                  })}
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>
                )}
                {confirmPassword && newPassword === confirmPassword && (
                  <p className="text-sm text-green-600 mt-1">Sifreler eslesiyor</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sifreyi Guncelle
                </button>
              </div>
            </form>
          </div>

          {/* Hesap Bilgileri */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Hesap Bilgileri</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-sm text-gray-600">Hesap Turu</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{user?.role}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-sm text-gray-600">Kayit Tarihi</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(user?.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm text-gray-600">Hesap Durumu</span>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Aktif
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Firma Bilgileri</h3>
              <p className="text-sm text-gray-500 mt-1">
                {user?.isDemo ? 'Demo hesapta firma bilgileri degistirilemez' : 'Lisansli firma bilgileriniz burada guncellenir'}
              </p>
            </div>
            <form onSubmit={onSubmitCompany} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firma Adi</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={companyForm.name}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yetkili Kisi</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={companyForm.owner_name}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, owner_name: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={companyForm.phone}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firma E-postasi</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      value={companyForm.email}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vergi Dairesi</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={companyForm.tax_office}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, tax_office: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vergi No</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={companyForm.tax_number}
                      disabled={user?.isDemo}
                      onChange={(e) => setCompanyForm({ ...companyForm, tax_number: e.target.value })}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                  <textarea
                    value={companyForm.address}
                    disabled={user?.isDemo}
                    onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md min-h-[96px]"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={companyLoading || user?.isDemo}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {companyLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Firma Bilgilerini Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
